import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../context/ThemeContext';
import { socialLinks } from '../data/mockData';

const DependencyGraph = () => {
  const { isDark } = useTheme();
  const svgRef = useRef(null);
  const simulationRef = useRef(null);

  const getSocialUrl = (name) => {
    const link = socialLinks.find(l => l.name === name);
    return link ? link.url : '#';
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const width = 300;
    const height = 280;

    // Clear previous content
    svg.selectAll('*').remove();

    // Define nodes
    const nodes = [
      { id: 'AS', label: 'AS', type: 'main', radius: 28 },
      { id: 'Social', label: 'Social', type: 'social', radius: 22 },
      { id: 'LinkedIn', label: 'in', type: 'leaf', radius: 16, url: getSocialUrl('LinkedIn') },
      { id: 'Twitter', label: 'X', type: 'leaf', radius: 16, url: getSocialUrl('Twitter') },
      { id: 'Instagram', label: 'IG', type: 'leaf', radius: 16, url: getSocialUrl('Instagram') },
    ];

    // Define links
    const links = [
      { source: 'AS', target: 'Social' },
      { source: 'Social', target: 'LinkedIn' },
      { source: 'Social', target: 'Twitter' },
      { source: 'Social', target: 'Instagram' },
    ];

    // Create force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(60).strength(0.8))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.radius + 10));

    simulationRef.current = simulation;

    // Create container group
    const g = svg.append('g');

    // Create links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', isDark ? 'rgba(182, 180, 189, 0.4)' : 'rgba(0, 0, 0, 0.2)')
      .attr('stroke-width', 2);

    // Create node groups
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', d => `node node-${d.type}`)
      .style('cursor', 'grab')
      .call(drag(simulation));

    // Add circles to nodes
    node.append('circle')
      .attr('r', d => d.radius)
      .attr('fill', d => {
        if (d.type === 'main') return isDark ? '#1a1a1a' : '#f1f5f9';
        if (d.type === 'social') return '#f46c38';
        return isDark ? '#2a2a2a' : '#e2e8f0';
      })
      .attr('stroke', d => {
        if (d.type === 'main') return isDark ? '#fff' : '#151312';
        if (d.type === 'social') return '#f46c38';
        return isDark ? 'rgba(182, 180, 189, 0.5)' : 'rgba(0, 0, 0, 0.2)';
      })
      .attr('stroke-width', d => d.type === 'main' ? 2 : 1.5);

    // Add labels to nodes
    node.append('text')
      .text(d => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-family', 'Poppins, sans-serif')
      .attr('font-weight', '600')
      .attr('font-size', d => {
        if (d.type === 'main') return '14px';
        if (d.type === 'social') return '9px';
        return '9px';
      })
      .attr('fill', d => {
        if (d.type === 'main') return isDark ? '#fff' : '#151312';
        if (d.type === 'social') return '#fff';
        return isDark ? '#998f8f' : '#64748b';
      })
      .style('pointer-events', 'none');

    // Add hover effects for leaf nodes (social links)
    node.filter(d => d.type === 'leaf')
      .on('mouseenter', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('fill', '#f46c38')
          .attr('stroke', '#f46c38');
        d3.select(this).select('text')
          .transition()
          .duration(200)
          .attr('fill', '#fff');
      })
      .on('mouseleave', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('fill', isDark ? '#2a2a2a' : '#e2e8f0')
          .attr('stroke', isDark ? 'rgba(182, 180, 189, 0.5)' : 'rgba(0, 0, 0, 0.2)');
        d3.select(this).select('text')
          .transition()
          .duration(200)
          .attr('fill', isDark ? '#998f8f' : '#64748b');
      })
      .on('click', function(event, d) {
        if (d.url) {
          window.open(d.url, '_blank', 'noopener,noreferrer');
        }
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      // Keep nodes within bounds
      nodes.forEach(d => {
        d.x = Math.max(d.radius, Math.min(width - d.radius, d.x));
        d.y = Math.max(d.radius, Math.min(height - d.radius, d.y));
      });

      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function drag(simulation) {
      function dragstarted(event, d) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
        d3.select(this).style('cursor', 'grabbing');
      }

      function dragged(event, d) {
        d.fx = event.x;
        d.fy = event.y;
      }

      function dragended(event, d) {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
        d3.select(this).style('cursor', 'grab');
      }

      return d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended);
    }

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [isDark]);

  return (
    <div className="dependency-graph-container">
      <div className="graph-header">
        <h3 className="graph-title" data-testid="graph-title">
          Ongoing development; as a dependency graph
        </h3>
        <div className="graph-live" data-testid="graph-live-badge">
          <span className="graph-live-dot" />
          <span className="graph-live-text">LIVE</span>
        </div>
      </div>
      <div className="graph-wrapper">
        <svg 
          ref={svgRef} 
          className="graph-svg" 
          width="300" 
          height="280"
          viewBox="0 0 300 280"
        />
        <p className="graph-hint" data-testid="graph-hint">drag a node · hover to trace the edges</p>
      </div>

      <style jsx>{`
        .dependency-graph-container {
          padding: 16px 0;
          max-width: 420px;
          margin: 0 auto;
        }

        .graph-title {
          font-family: 'Poppins', sans-serif;
          font-size: 11px;
          font-weight: 500;
          color: ${isDark ? '#6a6b6e' : '#94a3b8'};
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin: 0;
          text-align: left;
        }

        .graph-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        .graph-live {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .graph-live-dot {
          width: 6px;
          height: 6px;
          background: #f46c38;
          border-radius: 50%;
          animation: graphBlink 1.5s infinite;
        }

        @keyframes graphBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .graph-live-text {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 700;
          color: #f46c38;
          letter-spacing: 0.08em;
        }

        .graph-wrapper {
          position: relative;
        }

        .graph-svg {
          width: 100%;
          max-width: 300px;
          height: auto;
          display: block;
          margin: 0 auto;
        }

        .graph-hint {
          font-family: 'Poppins', sans-serif;
          font-size: 10px;
          color: ${isDark ? '#4a4a4a' : '#94a3b8'};
          text-align: left;
          margin: 8px 0 0 0;
          font-style: italic;
        }

        @media (max-width: 1024px) {
          .dependency-graph-container {
            margin-top: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default DependencyGraph;
